import { Paper, Title, Text } from '@mantine/core';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import type { Asset } from '@/types/models';

interface AssetStatusChartProps {
  assets: Asset[];
}

const STATUS_COLORS: Record<string, string> = {
  Available: '#51cf66',
  Assigned: '#339af0',
  Maintenance: '#ffd43b',
  Retired: '#868e96',
  Lost: '#ff6b6b',
};

export function AssetStatusChart({ assets }: AssetStatusChartProps) {
  // Count assets by status
  const statusCounts = assets.reduce(
    (acc, asset) => {
      acc[asset.status] = (acc[asset.status] || 0) + 1;
      return acc;
    },
    {} as Record<string, number>
  );

  // Convert to chart data
  const chartData = Object.entries(statusCounts).map(([status, count]) => ({
    name: status,
    value: count,
  }));

  if (chartData.length === 0) {
    return (
      <Paper withBorder p="lg" radius="md">
        <Title order={3} mb="md">
          Assets by Status
        </Title>
        <Text c="dimmed" ta="center" py="xl">
          No assets to display
        </Text>
      </Paper>
    );
  }

  return (
    <Paper withBorder p="lg" radius="md">
      <Title order={3} mb="md">
        Assets by Status
      </Title>
      <ResponsiveContainer width="100%" height={300}>
        <PieChart>
          <Pie
            data={chartData}
            cx="50%"
            cy="50%"
            labelLine={false}
            label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
            outerRadius={80}
            fill="#8884d8"
            dataKey="value"
          >
            {chartData.map((entry) => (
              <Cell key={entry.name} fill={STATUS_COLORS[entry.name] || '#868e96'} />
            ))}
          </Pie>
          <Tooltip />
          <Legend />
        </PieChart>
      </ResponsiveContainer>
    </Paper>
  );
}
